import React from 'react';
import { Box, Chip, ListItemButton, ListItemText, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { CH_TYPE } from '../../styles/appUi';
import type { AppColorTokens } from '../../styles/colorTokens';
// The ONE interval formatter. 420 -> '7H', not a hand-rolled hours/24 ladder that would
// disagree with the chart's own timeframe chip for a custom interval.
import { intervalLabel } from '../../utils/intervalCatalog';

/**
 * SymbolPaneRow — one row of the Charts symbol pane, in one of four shapes.
 *
 * WHY THIS IS NOT ONE TEMPLATE ANY MORE
 *
 * The pane drew every filter the same way: symbol / change% / exchange. That is the right
 * row for browsing NSE, and the wrong row for almost everything else, because the column
 * that made you PICK the filter was the one column it never showed. You chose PendEntry to
 * see armed levels and got a change%; you chose OpenPos to look at your positions and got
 * a change%; you chose an alert filter to see what fired and got no action and no time.
 *
 * The data was never missing. Every branch of `loadSymbolsByMode` fetched it and then threw
 * it away one line later, because `SymbolItem` was `{symbol, exchange}` and nothing else
 * could survive the trip. Widening that type is most of this feature.
 *
 * ROW HEIGHT is constant within a shape (28px for 'market', 40px for the two-line shapes),
 * so scrolling a list never jumps. It differs BETWEEN shapes, which is unavoidable once a
 * row carries a second line, and unnoticeable because the pane scroll resets to the top on
 * every filter change anyway.
 *
 * THE PANE IS 190px WIDE by default (160-480 resizable). That is the binding constraint:
 * symbol + type + time does not fit on one line beside a star and an exchange chip at that
 * width, which is why the meta shapes use a second line rather than more columns.
 *
 * `data-sym-row` MUST stay on the ListItemButton: `revealSymRow` and the ArrowUp/ArrowDown
 * keyboard navigation both find rows by querying that attribute.
 */

export type SymbolRowShape = 'market' | 'signal' | 'position' | 'level';

export interface SymbolRowItem {
  symbol: string;
  exchange: string;
  name?: string;
  action?: string;
  /**
   * WHEN it fired — the START OF THE BAR, epoch ms, when the engine recorded one. Falls
   * back to the moment the alert was written; `atIsBar` says which you are looking at.
   */
  at?: number;
  /**
   * True when `at` is a real candle boundary rather than the time the scan happened to run.
   * A TradingView webhook has no bar to point at, and the row must not imply it does.
   */
  atIsBar?: boolean;
  /** When the alert was actually RECORDED. Disclosed on hover so the scan lag is visible. */
  firedAt?: number;
  /** The scan interval in minutes (240 = 4h), as the engine evaluated it. */
  intervalMinutes?: number;
  /** The price at the moment the signal fired. */
  alertPrice?: number | null;
  note?: string;
  /**
   * What the AI already thinks of this symbol, if anything is on file.
   *
   * Filled from ONE batch read for the whole list, never per row, and it never triggers an
   * assessment — a grid that assessed itself would spend money by being looked at. Absent means
   * nothing is stored, and the row simply shows nothing rather than an empty placeholder.
   */
  ai?: { grade: string | null; state: string | null; direction: string | null; at: string; source: string } | null;
  pnl?: number | null;
  side?: string;
  qty?: number | null;
  level?: number | null;
}


/** Grade colour, matching the AI Desk so one letter means one thing across the app. */
function gradeTone(g: string | null | undefined, C: any): string {
  if (!g) return C.dim;
  if (g.startsWith('A')) return C.bull;
  if (g.startsWith('B')) return C.neutral;
  if (g === 'C') return C.warn;
  return C.bear;
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "09 Sep 16:45" — the WALL-CLOCK time, in IST.
 *
 * A relative age ("5h") is the wrong unit for the job this column does. The point of the
 * time on an alert row is to find the bar it belongs to on the chart, and "5h ago" forces
 * the reader to do arithmetic against a clock that keeps moving while they do it. An
 * absolute stamp lines up with the chart's own axis directly.
 *
 * IST SPECIFICALLY, not the browser's timezone. The chart's axis is IST by construction —
 * `TradingChart` stores IST-shifted UTC seconds and reads them back with `getUTC*`, so a
 * bar labelled 07:30 is 07:30 IST wherever you open it. Formatting this stamp in local
 * time would make the two agree only for a viewer who happens to be in India, and the
 * whole purpose of the column is that it matches the axis.
 *
 * Same shape the OHLC legend uses for an intraday bar, so the two read as one system. The
 * year is omitted deliberately: the alert windows on offer top out at 30 days.
 */
export function stampOf(at?: number): string {
  if (!at || !Number.isFinite(at)) return '';
  // +5:30 applied to the true UTC instant, then read back with getUTC* — exactly the
  // trick TradingChart uses, so the two cannot drift apart.
  const d = new Date(at + 5.5 * 3600_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())} ${MON[d.getUTCMonth()]} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/** Full IST date-time for a hover title, where there is room to be unambiguous. */
export function stampLongOf(at?: number): string {
  if (!at || !Number.isFinite(at)) return '';
  const d = new Date(at + 5.5 * 3600_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())} ${MON[d.getUTCMonth()]} ${d.getUTCFullYear()} `
    + `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} IST`;
}

/** Compact price, at the precision the instrument actually quotes. */
function px(v: number, fmt: (n: number) => string): string {
  return fmt(v);
}

/** Bullish / bearish / neutral for an action word, whichever vocabulary it comes from. */
function toneOf(action: string | undefined, C: AppColorTokens): string {
  const a = String(action || '').toUpperCase();
  if (a === 'BUY' || a === 'LONG' || a === 'COVER') return C.bull;
  if (a === 'SHORT' || a === 'SELL') return C.bear;
  // Blast patterns (Inside / Engulfing) are direction-neutral on their own.
  return C.neutral;
}

interface Props {
  item: SymbolRowItem;
  shape: SymbolRowShape;
  selected: boolean;
  isDark: boolean;
  C: AppColorTokens;
  /** Today's % move, or null when the feed carries none for this symbol. */
  pct: number | null;
  /** Last traded price, from the same %-changes feed. Drives the distance-to-trigger
   *  figure on the 'level' shape; null when that feed carries no price for the symbol. */
  ltp: number | null;
  /** Paired names for the two RWA filters (SPY · SPYXUSD). Empty everywhere else. */
  pairs: string[];
  isFav: boolean;
  /** Formats a price at the instrument's own precision. */
  fmtPrice: (n: number) => string;
  onClick: () => void;
  onToggleFav: () => void;
}

const SymbolPaneRow: React.FC<Props> = ({
  item, shape, selected, isDark, C, pct, ltp, pairs, isFav, fmtPrice, onClick, onToggleFav,
}) => {
  const pctLabel = pct == null ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
  const pctCol = pct == null ? 'text.disabled' : pct >= 0 ? '#26a69a' : '#ef5350';
  /**
   * Row height, by how many lines the shape actually draws.
   *
   * 'signal' grows to three ONLY when there is an alert price to show — a Blast mover or a
   * watchlist flag has no price, and giving those rows a blank third line would waste a
   * third of the pane. Height stays constant WITHIN a filter, which is what stops the list
   * jumping while you scroll it.
   */
  const lines = shape === 'market' ? 1
    : (shape === 'signal' && item.alertPrice != null) ? 3
    : 2;
  const twoLine = lines > 1;

  const num = { fontVariantNumeric: 'tabular-nums' as const };
  const metaSx = {
    fontSize: CH_TYPE.micro, color: 'text.secondary', lineHeight: 1.1,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  };

  /** The right-hand figure on line 1 — the reason this filter was chosen. */
  const primary = (() => {
    if (shape === 'position') {
      const v = item.pnl;
      return (
        <Typography sx={{
          fontSize: CH_TYPE.micro, fontWeight: 700, ...num,
          color: v == null ? 'text.disabled' : v >= 0 ? C.bull : C.bear,
        }}>
          {v == null ? '—' : `${v >= 0 ? '+' : ''}${Math.round(v).toLocaleString('en-IN')}`}
        </Typography>
      );
    }
    if (shape === 'level') {
      return (
        <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 700, ...num, color: 'text.primary' }}>
          {item.level == null ? '—' : fmtPrice(item.level)}
        </Typography>
      );
    }
    // 'market' and 'signal' both lead with the move. A GLOBAL/RWA row whose feed carries no
    // percent spends that width on a dash for nothing, so it is dropped there only.
    if (pairs.length && pct == null) return null;
    return (
      <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 700, color: pctCol, ...num }}>
        {pctLabel}
      </Typography>
    );
  })();

  /** Line 2 — the qualifier. Absent for 'market'. */
  const meta = (() => {
    if (shape === 'market') return null;
    if (shape === 'signal') {
      const stamp = stampOf(item.at);
      const tf = item.intervalMinutes != null ? intervalLabel(item.intervalMinutes) : '';
      const ap = item.alertPrice;
      // Move since the signal. This is the number the row exists to make obvious: an alert
      // whose price has already run 3% is a different proposition from one that has not.
      const drift = (ap != null && ap > 0 && ltp != null && ltp > 0)
        ? ((ltp - ap) / ap) * 100
        : null;

      // Hover carries what the row cannot: whether the stamp is a BAR or a record time, the
      // scan lag, and full precision. Hiding the lag would be the same sin as the freshness
      // stamp that advanced on a timer — say what the number is.
      const barAt = item.at;
      const scanAt = item.firedAt;
      // How long after the bar opened the scan actually recorded it. Stated plainly rather
      // than left for the reader to infer from two timestamps.
      const lagMin = (item.atIsBar && barAt != null && scanAt != null && scanAt > barAt)
        ? Math.round((scanAt - barAt) / 60000)
        : null;
      const title = [
        barAt != null ? `${item.atIsBar ? 'Candle start' : 'Alert recorded'}: ${stampLongOf(barAt)}` : '',
        lagMin != null ? `Scan recorded it ${lagMin} min later, at ${stampLongOf(scanAt)}` : '',
        (!item.atIsBar && barAt != null)
          ? 'This source reports no candle boundary (e.g. a TradingView webhook), so this is when the alert arrived, not when a bar opened.'
          : '',
        tf ? `Scan interval: ${tf}` : '',
        ap != null ? `Alert price: ${fmtPrice(ap)}` : '',
        ltp != null ? `Last price: ${fmtPrice(ltp)}` : '',
        (drift != null) ? `Moved ${drift >= 0 ? '+' : ''}${drift.toFixed(2)}% since the alert` : '',
        item.note ? `Strategy: ${item.note}` : '',
        item.ai?.grade
          ? `AI: grade ${item.ai.grade}${item.ai.state ? ` — ${item.ai.state.replace(/_/g, ' ').toLowerCase()}` : ''}`
            + `${item.ai.direction && item.ai.direction !== 'NONE' ? ` (${item.ai.direction})` : ''}, `
            + `formed ${stampLongOf(Date.parse(item.ai.at))}`
            + `${item.ai.source === 'NIGHTLY_BOARD' ? ' on the nightly board, not on this signal' : ' on this signal'}`
          : '',
      ].filter(Boolean).join('\n');

      return (
        <Box title={title} sx={{ minWidth: 0 }}>
          {/* line A — what fired, on what interval, on which bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
            {!!item.action && (
              <Box component="span" sx={{
                fontSize: CH_TYPE.micro, fontWeight: 800, letterSpacing: 0.2,
                color: toneOf(item.action, C), flexShrink: 0,
              }}>{item.action}</Box>
            )}
            {!!tf && (
              <Box component="span" sx={{
                fontSize: CH_TYPE.micro, fontWeight: 700, color: C.neutral, flexShrink: 0,
              }}>{tf}</Box>
            )}
            {/* The AI's grade, when one is already on file. Read-only: rendered from a single
                batch read of what is STORED, and pressing it does nothing — asking is still a
                deliberate click on the Ask AI chip elsewhere. Dimmed when it came from last
                night's board rather than from this signal, because "the AI looked at this symbol"
                and "the AI looked at this signal" are different claims. */}
            {!!item.ai?.grade && (
              <Box component="span" sx={{
                fontSize: CH_TYPE.micro, fontWeight: 800, flexShrink: 0, px: 0.35,
                borderRadius: 0.5, lineHeight: 1.5,
                border: `1px solid ${gradeTone(item.ai.grade, C)}66`,
                color: gradeTone(item.ai.grade, C),
                opacity: item.ai.source === 'NIGHTLY_BOARD' ? 0.75 : 1,
              }}>{item.ai.grade}</Box>
            )}
            {/* The strategy name yields first: on a 190px pane the timestamp is the column
                the user came for, and a truncated name still identifies the strategy. */}
            {!!item.note && <Typography sx={{ ...metaSx, flex: '1 1 auto', minWidth: 0 }}>{item.note}</Typography>}
            {!!stamp && (
              <Typography sx={{
                ...metaSx, ...num, flexShrink: 0, ml: 'auto',
                // A record time is NOT a bar boundary; it is dimmed so the two are
                // distinguishable at a glance rather than only in the tooltip.
                color: item.atIsBar ? 'text.primary' : 'text.secondary',
                fontStyle: item.atIsBar ? 'normal' : 'italic',
              }}>{stamp}</Typography>
            )}
          </Box>
          {/* line B — alert price → last price, and the move between them */}
          {ap != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0, mt: 0.1 }}>
              <Typography sx={{ ...metaSx, ...num, flexShrink: 0 }}>
                {px(ap, fmtPrice)}
                <Box component="span" sx={{ color: 'text.disabled', mx: 0.3 }}>→</Box>
                {ltp == null ? '—' : px(ltp, fmtPrice)}
              </Typography>
              {drift != null && (
                <Typography sx={{
                  ...metaSx, ...num, flexShrink: 0, ml: 'auto', fontWeight: 700,
                  color: drift >= 0 ? C.bull : C.bear,
                }}>{`${drift >= 0 ? '+' : ''}${drift.toFixed(2)}%`}</Typography>
              )}
            </Box>
          )}
        </Box>
      );
    }
    if (shape === 'position') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
          {!!item.side && (
            <Box component="span" sx={{
              fontSize: CH_TYPE.micro, fontWeight: 800, color: toneOf(item.side, C), flexShrink: 0,
            }}>{item.side}</Box>
          )}
          <Typography sx={{ ...metaSx, ...num, ml: 'auto', flexShrink: 0 }}>
            {item.qty == null ? '' : `×${item.qty}`}
          </Typography>
          <Typography sx={{ ...metaSx, flexShrink: 0 }}>{item.exchange}</Typography>
        </Box>
      );
    }
    // 'level' — an armed level's only real question is how far price still has to travel,
    // so show the gap rather than making the user do the arithmetic against the axis.
    const gapPct = (item.level != null && item.level > 0 && ltp != null && ltp > 0)
      ? ((item.level - ltp) / ltp) * 100
      : null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
        {!!item.side && (
          <Box component="span" sx={{
            fontSize: CH_TYPE.micro, fontWeight: 800, color: toneOf(item.side, C), flexShrink: 0,
          }}>{item.side}</Box>
        )}
        {gapPct != null && (
          <Typography sx={{ ...metaSx, ...num, color: 'text.primary', flexShrink: 0 }}>
            {`${gapPct >= 0 ? '+' : ''}${gapPct.toFixed(2)}% away`}
          </Typography>
        )}
        {!!item.note && <Typography sx={{ ...metaSx, flex: 1 }}>{item.note}</Typography>}
        <Typography sx={{ ...metaSx, flexShrink: 0, ml: 'auto' }}>{item.exchange}</Typography>
      </Box>
    );
  })();

  return (
    <ListItemButton
      data-sym-row={`${item.symbol}-${item.exchange}`}
      selected={selected}
      onClick={onClick}
      sx={{
        py: twoLine ? 0.3 : 0.2, px: 0.8,
        minHeight: lines === 3 ? 52 : lines === 2 ? 40 : 28,
        position: 'relative',
        // R120 — make the active symbol unmistakable: deep accent fill, a bright left
        // accent bar, and bold-italic symbol text.
        '&.Mui-selected, &.Mui-selected:hover': {
          bgcolor: isDark ? 'rgba(41,98,255,0.30)' : 'rgba(41,98,255,0.16)',
          boxShadow: `inset 3px 0 0 ${isDark ? '#5b8bff' : '#2962ff'}`,
        },
      }}
    >
      <ListItemText disableTypography primary={
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.4 }}>
            <Box
              component="span"
              role="button"
              title={isFav ? 'Remove from favourites' : 'Mark as favourite ★'}
              onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
              sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', mr: 0.1, flexShrink: 0 }}
            >
              {isFav
                ? <StarIcon sx={{ fontSize: 13, color: '#ffb300' }} />
                : <StarBorderIcon sx={{ fontSize: 13, color: 'text.disabled', '&:hover': { color: '#ffb300' } }} />}
            </Box>
            {/* title = full symbol on hover — long names (MCX futures, F&O stocks) ellipsize
                in the narrow pane. In the two RWA modes the row also carries the name the
                OTHER list knows it by (SPY · SPYXUSD). Same instrument, two venues, two
                names — and a list showing only one of them read as "the symbol is missing"
                to anyone scanning for the other. */}
            <Typography
              title={pairs.length ? `${item.symbol} · ${pairs.join(', ')}` : (item.name ? `${item.symbol} · ${item.name}` : item.symbol)}
              sx={{
                fontSize: selected ? '0.76rem' : '0.72rem',
                fontWeight: selected ? 800 : 700,
                fontStyle: selected ? 'italic' : 'normal',
                color: selected ? (isDark ? '#e3ecff' : '#13294b') : 'text.primary',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
              }}
            >
              {item.symbol}
              {pairs.length > 0 && (
                <Box component="span" sx={{ fontWeight: 500, color: 'text.disabled', ml: 0.4 }}>
                  · {pairs.join(', ')}
                </Box>
              )}
            </Typography>
            {primary}
            {/* The exchange chip is line-1 furniture only on the market shape. The meta
                shapes move it to line 2, where there is room, and spend the line-1 width
                on the figure that actually matters. */}
            {shape === 'market' && (
              <Chip label={item.exchange} size="small" sx={{
                fontSize: CH_TYPE.micro, height: 18, fontWeight: 600, ml: 0.3,
                '& .MuiChip-label': { px: 0.4 },
              }} />
            )}
          </Box>
          {meta && <Box sx={{ pl: 1.9, mt: 0.1 }}>{meta}</Box>}
        </Box>
      } />
    </ListItemButton>
  );
};

export default SymbolPaneRow;
